import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import { siteInfo } from "@/mocks/site";

const productOptions = [
  "산업용 고무부품",
  "자동차용 고무부품",
  "방진고무",
  "패킹 및 가스켓",
  "압축성형 제품",
  "고무 금형 제품",
  "맞춤 제작 제품",
  "기타 (직접 입력)",
];

export default function QuotationPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);
    const formData = new FormData(form);
    if (honeypot) formData.delete("company_alt");

    try {
      await fetch("https://readdy.ai/api/form/d8tkigcal24muhn2r8a0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
    } catch {
      // silently handle
    }

    emailjs.sendForm("service_ml5rlis", "template_tyiwc9t", form).catch(() => {});

    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <SiteLayout navVariant="solid">
        <PageBanner
          title="견적 요청"
          subtitle="견적 요청이 접수되었습니다"
          breadcrumb={[
            { label: "고객지원", to: "/support" },
            { label: "견적 요청" },
          ]}
          bgImage="https://readdy.ai/api/search-image?query=Modern%20clean%20industrial%20office%20interior%20with%20professional%20atmosphere%2C%20engineering%20consultation%20desk%20with%20technical%20documents%2C%20warm%20natural%20lighting%2C%20corporate%20manufacturing%20business%20environment%2C%20clean%20organized%20workspace&width=1600&height=680&seq=krubber-quotation-banner&orientation=landscape"
        />
        <section className="py-20 bg-background-50">
          <div className="max-w-[640px] mx-auto px-6 text-center">
            <span className="w-20 h-20 mx-auto rounded-2xl bg-primary-100 flex items-center justify-center">
              <i className="ri-check-line text-primary-600 text-3xl" />
            </span>
            <h2 className="mt-6 text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
              견적 요청이 접수되었습니다
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              담당 엔지니어가 도면과 요청 내용을 검토한 후 <strong className="text-foreground-950">2~3 영업일 이내</strong>에
              견적서를 이메일로 발송해 드립니다. 긴급한 사항은 <strong className="text-foreground-950">{siteInfo.tel}</strong>로 연락 부탁드립니다.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/products"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                제품 보러가기
              </Link>
              <Link
                to="/technical"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line" />
                기술자료 보기
              </Link>
            </div>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title="견적 요청"
        subtitle="도면과 요구사항을 보내주시면 최적의 견적을 신속하게 제공해 드립니다"
        breadcrumb={[
          { label: "고객지원", to: "/support" },
          { label: "견적 요청" },
        ]}
        bgImage="https://readdy.ai/api/search-image?query=Modern%20clean%20industrial%20office%20interior%20with%20professional%20atmosphere%2C%20engineering%20consultation%20desk%20with%20technical%20documents%2C%20warm%20natural%20lighting%2C%20corporate%20manufacturing%20business%20environment%2C%20clean%20organized%20workspace&width=1600&height=680&seq=krubber-quotation-banner&orientation=landscape"
      />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            {/* Left: Info */}
            <div className="lg:w-[420px] flex-shrink-0">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                견적 의뢰 안내
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                정확한 견적 산출을 위해 아래 정보를 함께 보내주시면 더욱 신속한 상담이 가능합니다.
              </p>

              <div className="mt-8 p-6 rounded-xl bg-background-100 border border-background-200/70">
                <p className="text-sm font-bold text-foreground-800 mb-4">견적 의뢰 시 필요 정보</p>
                <ul className="flex flex-col gap-3">
                  {[
                    "제품 도면 또는 스케치 (PDF, DWG, JPG)",
                    "필요 수량 (월 예상 소요량)",
                    "적용 환경 (온도, 접촉 유체, 압력 등)",
                    "요구 물성 (경도, 인장강도, 내열온도 등)",
                    "희망 납기",
                  ].map((info) => (
                    <li key={info} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-primary-500" />
                      </span>
                      {info}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 p-6 rounded-xl bg-accent-50 border border-accent-200/60">
                <p className="text-sm font-bold text-foreground-800 mb-3">견적 프로세스</p>
                <div className="flex flex-col gap-4">
                  {[
                    { step: 1, title: "문의 접수", desc: "전화, 이메일, 또는 견적요청 게시판을 통한 접수" },
                    { step: 2, title: "도면 검토", desc: "엔지니어의 도면/사양 검토 및 소재 추천" },
                    { step: 3, title: "견적서 발송", desc: "2~3 영업일 이내 견적서 이메일 발송" },
                    { step: 4, title: "협의 및 조정", desc: "수량·납기·사양 협의 및 최종 견적 확정" },
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-3">
                      <span className="w-8 h-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-xs font-extrabold flex-shrink-0">
                        {item.step}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{item.title}</p>
                        <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={siteInfo.telLink}
                  className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-colors cursor-pointer"
                >
                  <span className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-600" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">전화 문의</p>
                    <p className="text-sm font-bold text-foreground-950">{siteInfo.tel}</p>
                  </div>
                </a>
                <a
                  href={siteInfo.emailLink}
                  className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-colors cursor-pointer"
                >
                  <span className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">이메일 문의</p>
                    <p className="text-sm font-bold text-foreground-950">{siteInfo.email}</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Right: Form */}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                온라인 견적 요청
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                아래 양식을 작성해 주시면 담당 엔지니어가 확인 후 견적서를 발송해 드립니다.
                <span className="text-accent-500">*</span> 표시는 필수 입력 항목입니다.
              </p>

              <form data-readdy-form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
                {/* Row 1: Company + Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="company" className="block text-xs font-bold text-foreground-600 mb-2">
                      회사명 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="회사명을 입력하세요"
                    />
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-xs font-bold text-foreground-600 mb-2">
                      담당자명 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="담당자명을 입력하세요"
                    />
                  </div>
                </div>

                {/* Row 2: Phone + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold text-foreground-600 mb-2">
                      연락처 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="연락처를 입력하세요"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold text-foreground-600 mb-2">
                      이메일 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="이메일을 입력하세요"
                    />
                  </div>
                </div>

                {/* Product + Quantity */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="product_category" className="block text-xs font-bold text-foreground-600 mb-2">
                      제품 분야 <span className="text-accent-500">*</span>
                    </label>
                    <select
                      id="product_category"
                      name="product_category"
                      required
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors cursor-pointer"
                    >
                      <option value="">제품 분야를 선택하세요</option>
                      {productOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="product_name" className="block text-xs font-bold text-foreground-600 mb-2">
                      제품명 / 품번
                    </label>
                    <input
                      type="text"
                      id="product_name"
                      name="product_name"
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="제품명 또는 품번을 입력하세요"
                    />
                  </div>
                </div>

                {/* Quantity + Deadline */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="quantity" className="block text-xs font-bold text-foreground-600 mb-2">
                      필요 수량 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="quantity"
                      name="quantity"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="예) 월 10,000개 / 초도 500개"
                    />
                  </div>
                  <div>
                    <label htmlFor="deadline" className="block text-xs font-bold text-foreground-600 mb-2">
                      희망 납기
                    </label>
                    <input
                      type="text"
                      id="deadline"
                      name="deadline"
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="예) 2025년 8월 말 / ASAP"
                    />
                  </div>
                </div>

                {/* Requirements */}
                <div>
                  <label htmlFor="requirements" className="block text-xs font-bold text-foreground-600 mb-2">
                    요구사항 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="requirements"
                    name="requirements"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder="적용 환경, 요구 물성(경도, 내열온도, 내유성 등), 특이사항 등을 구체적으로 기재해 주세요."
                  />
                  <p className="mt-1 text-xs text-foreground-400">최대 500자까지 입력 가능합니다.</p>
                </div>

                {/* File Upload */}
                <div>
                  <label htmlFor="drawing_file" className="block text-xs font-bold text-foreground-600 mb-2">
                    도면 파일 첨부
                  </label>
                  <input
                    type="file"
                    id="drawing_file"
                    name="drawing_file"
                    className="w-full text-sm text-foreground-600 file:mr-4 file:py-2.5 file:px-5 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200 file:cursor-pointer"
                  />
                  <p className="mt-1 text-xs text-foreground-400">
                    PDF, DWG, DXF, JPG, PNG 파일 첨부 가능 (최대 10MB)
                  </p>
                </div>

                {/* Privacy consent */}
                <div className="flex items-start gap-3 mt-2">
                  <input
                    type="checkbox"
                    id="privacy_consent"
                    name="privacy_consent"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                  />
                  <label htmlFor="privacy_consent" className="text-xs text-foreground-600 leading-relaxed">
                    개인정보 수집 및 이용에 동의합니다. 수집된 개인정보는 견적 응대 목적으로만 사용되며,
                    관련 법령에 따라 안전하게 보관됩니다. <span className="text-accent-500">*</span>
                  </label>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hp-shield"
                />

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-4 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin" />
                      접수 중...
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" />
                      견적 요청하기
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}